/*
 * PDP lightbox — single-image arrow navigation (desktop only).
 *
 * Dawn's restyled ProductModal (.product-media-modal, centered card in
 * elome-product-lightbox.css) shows its images one at a time here: this IIFE injects ← / →
 * arrows and pages through the images on click. A MutationObserver on <body>
 * watches the modal's open state (Dawn toggles the `open` attribute on the
 * <product-modal>); when it opens we init (show only the first image + arrows),
 * when it closes we clean up (remove arrows, restore every image to display).
 * Strictly desktop: bails unless pointer:fine. Single-image products get no
 * arrows. Independent of every other IIFE in this file.
 */
(function () {
  'use strict';

  if (!window.matchMedia('(pointer: fine)').matches) return; // desktop only

  var ARROW_CLASS = 'elome-modal-arrow';
  var images = [];
  var current = 0;
  var wasOpen = false;
  var isOpen = false; // re-entrancy guard so init() can't stack arrows (Fix 1)
  var pendingSrc = null; // src of the gallery image last clicked (Fix 2)

  function getDialog() {
    return document.querySelector('.product-media-modal__dialog');
  }

  // Dawn's ProductModal.show() → ModalDialog.show() sets the `open` attribute on
  // the <product-modal class="product-media-modal"> (and removeAttribute on
  // hide). `[open]` selector finds whichever modal is open, robust to the modal
  // being moved to <body> on connect and to multiple modals on the page.
  function modalIsOpen() {
    return !!document.querySelector('.product-media-modal[open]');
  }

  // Show only the image at `index`; hide the rest.
  function showOnly(index) {
    images.forEach(function (img, i) {
      img.style.display = i === index ? 'block' : 'none';
    });
  }

  function goPrev() {
    current = current === 0 ? images.length - 1 : current - 1;
    showOnly(current);
  }

  function goNext() {
    current = current === images.length - 1 ? 0 : current + 1;
    showOnly(current);
  }

  // Arrow-key navigation while the modal is open (Fix 3). Escape is intentionally
  // ignored — Dawn's ModalDialog already closes the modal on Escape.
  function onKeydown(e) {
    if (images.length < 2 || !modalIsOpen()) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  }

  function makeArrow(side) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = ARROW_CLASS;
    btn.textContent = side === 'left' ? '‹' : '›';
    btn.style.position = 'fixed';
    if (side === 'left') {
      btn.style.left = '2rem';
    } else {
      btn.style.right = '2rem';
    }
    btn.style.top = '50%';
    btn.style.transform = 'translateY(-50%)';
    // Just the glyph — no background, border or box.
    btn.style.background = 'none';
    btn.style.border = 'none';
    btn.style.padding = '1rem';
    btn.style.margin = '0';
    btn.style.color = '#121212';
    btn.style.fontSize = '4rem';
    btn.style.lineHeight = '1';
    btn.style.cursor = 'none';
    btn.style.zIndex = '99997';
    return btn;
  }

  function init() {
    if (isOpen) return; // already inited for this open — no duplicate arrows (Fix 1)
    isOpen = true;

    var dialog = getDialog();
    if (!dialog) return;

    // Modal images only — exclude any thumbnail-strip images.
    images = Array.prototype.slice
      .call(document.querySelectorAll('.product-media-modal img'))
      .filter(function (el) {
        return !el.closest('.thumbnail-list');
      });

    // No images at all: nothing to do. (A single image still runs through here;
    // it just won't get arrows from the guarded injection below.)
    if (images.length === 0) return;

    // Start on the image the shopper actually clicked in the gallery (Fix 2).
    // Gallery and modal srcs differ only by width/version query params, so match
    // on the path (before `?`). Default to the first image.
    current = 0;
    if (pendingSrc) {
      var targetPath = pendingSrc.split('?')[0];
      for (var i = 0; i < images.length; i++) {
        if ((images[i].currentSrc || images[i].src).split('?')[0] === targetPath) {
          current = i;
          break;
        }
      }
    }

    showOnly(current);

    // Keyboard nav is live while the modal is open (Fix 3); removed on cleanup.
    document.addEventListener('keydown', onKeydown);

    // Inject the arrows into <body>, NOT the modal subtree. Dawn's ModalDialog
    // (global.js) adds a `pointerup` listener on .product-media-modal that calls
    // hide() for any mouse-up bubbling to it that isn't on deferred-media /
    // product-model. An arrow inside the modal triggers that on pointerup —
    // which fires before click — closing the modal before our handler runs, and
    // stopPropagation on `click` can't stop an already-dispatched pointerup. As
    // body siblings (position:fixed, z-index above the open modal's 101) the
    // arrows' events never bubble through the modal, so it stays open and the
    // click handlers fire normally.
    if (images.length > 1 && !document.querySelector('.' + ARROW_CLASS)) {
      var left = makeArrow('left');
      var right = makeArrow('right');

      left.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        goPrev();
      });
      right.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        goNext();
      });

      document.body.appendChild(left);
      document.body.appendChild(right);

      // Position the arrows 16px inside the modal card's actual edges (the card
      // width is auto, so it tracks the image).
      var rect = dialog.getBoundingClientRect();
      left.style.left = rect.left + 16 + 'px';
      left.style.transform = 'translateY(-50%)';
      right.style.right = window.innerWidth - rect.right + 16 + 'px';
      right.style.transform = 'translateY(-50%)';
    }
  }

  function cleanup() {
    document.removeEventListener('keydown', onKeydown);
    isOpen = false; // allow the next open to init again (Fix 1)
    // Arrows live on <body> now (see init), so remove them document-wide — else
    // the fixed-position glyphs would linger over the page after close.
    document.querySelectorAll('.' + ARROW_CLASS).forEach(function (arrow) {
      arrow.parentNode.removeChild(arrow);
    });
    var modal = document.querySelector('.product-media-modal');
    if (modal) {
      modal.querySelectorAll('img').forEach(function (img) {
        img.style.display = 'block';
      });
    }
    images = [];
    current = 0;
  }

  // Fix 2 — remember which gallery image / thumbnail was clicked, so init() can
  // open the modal on it. Capture phase, so it runs before Dawn opens the modal.
  // The toggle overlay usually intercepts the raw <img> click, so match on the
  // media-item / thumbnail container and read the <img> inside it.
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest(
        '.product__media-list img, .product__media-item, .thumbnail'
      );
      if (!trigger) return;
      var img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
      pendingSrc = img ? img.currentSrc || img.src : null;
    },
    true
  );

  // Fire init/cleanup on the modal's open→closed transitions. The callback is
  // cheap (one attribute read + a boolean compare), so a broad body observer is
  // fine. Dawn flags the open modal via the `open` attribute on <product-modal>.
  var observer = new MutationObserver(function () {
    var open = modalIsOpen();
    if (open && !wasOpen) {
      wasOpen = true;
      init();
    } else if (!open && wasOpen) {
      wasOpen = false;
      cleanup();
    }
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['open'],
    subtree: true
  });
})();
